import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, Autocomplete, Checkbox,
    TableRow, TextField, Button, CircularProgress, Chip, Stack, Grid, Tooltip, ListItemText
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Fastfood as FastfoodIcon, Info as InfoIcon } from '@mui/icons-material';
import ConfirmationDialog from '../../../ReusableDailog';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useReactToPrint } from 'react-to-print';
import PrintIcon from '@mui/icons-material/Print';
import UpdatePrint from './UpdatePrint';

const UpdateBomQty = () => {
    const [bomData, setBomData] = useState([]);
    const [eqColumns, setEqColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userId] = useState(sessionStorage.getItem('UserId'));
    const [saving, setSaving] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const globalProperty = useSelector(selectGlobalProperty);
    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const contentRef = useRef();
    const handlePrint = useReactToPrint({ contentRef });
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const foodBomIdFromUrl = queryParams.get('foodBomId');
    const [foodBomId, setFoodBomId] = useState(foodBomIdFromUrl ? Number(foodBomIdFromUrl) : null);

    useEffect(() => {
        fetchBomData();
    }, [globalProperty]);

    const fetchBomData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.post('MstFood/RetriveMstFoodBomItm', {
                FoodBomId: foodBomId,
                Flag: "",
                PropId: globalProperty,
            });

            if (response.data.Status === 0 && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                const firstItem = response.data.Data[0];

                const columns = [];
                Object.keys(firstItem).forEach(key => {
                    if (key.startsWith('EQNm') && key.match(/^EQNm\d+$/)) {
                        const num = parseInt(key.replace('EQNm', ''), 10);
                        const value = String(firstItem[key]).trim();
                        if (value !== '') {
                            columns.push({
                                index: num,
                                key: `EQ${num}`,
                                label: value,
                            });
                        }
                    }
                });

                columns.sort((a, b) => a.index - b.index);

                setEqColumns(columns);

                const normalized = response.data.Data.map(item => {
                    const row = {
                        ItmName: item.ItmName || '—',
                        ...item,
                    };

                    columns.forEach(({ key }) => {
                        row[key] = row[key] != null && !isNaN(Number(row[key])) ? Number(row[key]) : 0;
                    });

                    return row;
                });

                setBomData(normalized);
            } else {
                toast.warn(response.data.Message);
                setBomData([]);
                setEqColumns([]);
            }
        } catch (error) {
            toast.error("Failed to load BOM items");
            setBomData([]);
            setEqColumns([]);
        } finally {
            setLoading(false);
        }
    };

    const foodName = bomData.length > 0 ? bomData[0]?.FoodName || '—' : 'NA';

    const handleQtyChange = (rowIndex, eqKey, value) => {
        setBomData(prev => {
            const next = [...prev];
            next[rowIndex] = {
                ...next[rowIndex],
                [eqKey]: value === '' ? 0 : Number(value),
            };
            return next;
        });
    };

    const handleSave = async () => {
        if (bomData.length === 0 || eqColumns.length === 0) {
            toast.info("Nothing to save");
            return;
        }

        setSaving(true);

        try {
            const payload = bomData.map(item => {
                const base = {
                    FoodBomItmId: item.FoodBomItmId || 0,
                    FoodBomDtlId: item.FoodBomDtlId || 0,
                    FoodBomId: item.FoodBomId || 23,
                    PropId: globalProperty,
                    ItmId: item.ItmId || 0,
                    StdQty: item.StdQty ?? 200,
                    Remark: item.Remark || "",
                    CreatedBy: userId || 1,
                    UpdatedBy: userId,
                };
                eqColumns.forEach(({ key }) => {
                    base[key] = Number(item[key]) || 0;
                });

                eqColumns.forEach(({ index, label }) => {
                    const nmKey = `EQNm${index}`;
                    base[nmKey] = item[nmKey] || label;
                });

                return base;
            });

            const response = await axiosInstance.post('MstFood/UpsertMstFoodBomItm', payload);

            if (response.data.Status === 0) {
                toast.success("BOM quantities updated successfully");
                setTimeout(() => {
                    fetchBomData();
                }, 2000)
            } else {
                toast.error(response.data.Message || "Save operation failed");
            }
        } catch (error) {
            toast.error(error.response?.data?.Message || "Failed to save BOM quantities");
        } finally {
            setSaving(false);
        }
    };

    const handleKeyPress = (e, rowIndex, colIndex) => {
        const { key } = e;

        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(key)) {
            let newRow = rowIndex;
            let newCol = colIndex;

            if (key === 'ArrowUp' && rowIndex > 0) {
                newRow = rowIndex - 1;
            } else if (key === 'ArrowDown' || key === 'Enter') {
                if (rowIndex < bomData.length - 1) {
                    newRow = rowIndex + 1;
                }
            } else if (key === 'ArrowLeft' && colIndex > 0) {
                newCol = colIndex - 1;
            } else if (key === 'ArrowRight' && colIndex < eqColumns.length - 1) {
                newCol = colIndex + 1;
            }

            if (inputRefs.current[newRow] && inputRefs.current[newRow][newCol]) {
                inputRefs.current[newRow][newCol].focus();
            }
            e.preventDefault();
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    const handleConfirmDelete = async () => {
        setOpenConfirmDialog(false);

        try {
            const response = await axiosInstance.post("MstFood/DeleteMstFoodBomItm", {
                FoodBomId: foodBomId,
                Flag: '',
                PropId: globalProperty
            });
            if (response.data.Status === 0) {
                toast.success(response.data.Message || 'Record Deleted.');
                setTimeout(() => {
                    fetchBomData();
                }, 2000)
            } else {
                toast.warn(response.data.Message || 'Failed to delete the record.');
            }
        } catch (error) {
            toast.error("Error while deleting");
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress size="3rem" />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '100%', mx: 'auto' }}>
            <ToastContainer />
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap="nowrap"
                gap={2}
            >
                <Typography variant="h6" fontWeight={600} sx={{ color: '#635bff' }}>
                    Recipe Ingredients Quantity
                </Typography>

                <Stack direction="row" spacing={1.5}>
                    <Button size='small'
                        variant="outlined"
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        disabled={saving}
                        onClick={handleSave}
                        sx={{ borderColor: '#16a34a', color: '#16a34a', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#15803d', background: 'rgba(22,163,74,0.1)', } }}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>

                    <Button size='small'
                        variant="outlined"
                        startIcon={<DeleteIcon color="error" />}
                        onClick={() => setOpenConfirmDialog(true)}
                        sx={{ borderColor: '#ef4444', color: '#ef4444', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#b91c1c', background: 'rgba(239,68,68,0.1)' } }}
                    >
                        Delete
                    </Button>

                    <Tooltip title='Check Table' arrow>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<TableRowsIcon fontSize="small" />}
                                onClick={() => navigate('/incbomtable')}
                                sx={{ borderColor: '#7c3aed', color: '#7c3aed', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#6d28d9', background: 'rgba(124,58,237,0.1)' } }}
                            >
                                Table
                            </Button>
                        </span>
                    </Tooltip>

                    <Button size='small'
                        variant='outlined'
                        onClick={handlePrint}
                        sx={{ borderColor: '#0d9488', color: '#0d9488', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#0891b2', background: 'rgba(13,148,136,0.1)' } }}
                    >
                        <PrintIcon />
                    </Button>

                    <Tooltip title='Move to basic Ratio' arrow>
                        <span>
                            <Button size='small'
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(`/basicratio?foodBomId=${foodBomId}`)}
                                sx={{ borderColor: '#f59e0b', color: '#f59e0b', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#b45309', background: 'rgba(245,158,11,0.1)', } }}
                            >
                                Ratio
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title='Close Window' arrow>
                        <span>
                            <Button size='small'
                                variant="outlined"
                                startIcon={<CloseIcon />}
                                onClick={() => window.close()}
                                sx={{ borderColor: '#940d0d', color: '#941d0d', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#b20e08', background: 'rgba(13,148,136,0.1)' } }}
                            >
                                Close
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={12}>
                    <Stack direction="row" spacing={2} mb={2} alignItems="center" gap={4} justifyContent='flex-start'>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <InfoIcon color="primary" />
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                                FoodBomId:
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                                {foodBomId}
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <FastfoodIcon color="success" />
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                                Food Name:
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                                {foodName}
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <InfoIcon color="warning" />
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                                Quantity:
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                                {bomData[0]?.EQNm0}
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                            <ArrowUpwardIcon color="secondary" />
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                                Inc Qty:
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                                {bomData[0]?.StdMealIncre}
                            </Typography>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>

            <Paper
                elevation={2}
                sx={{
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <TableContainer sx={{ maxHeight: 640, overflowX: 'auto' }}>
                    <Table stickyHeader size="small"
                        sx={{
                            '& .MuiTableCell-root': {
                                paddingTop: '3.2px',
                                paddingBottom: '3.2px',
                                paddingLeft: '4.4px',
                                paddingRight: '4.4px',
                                fontSize: '0.85rem',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell
                                    sx={{
                                        minWidth: 140,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 15,
                                        bgcolor: 'grey.100',
                                        fontWeight: 700,
                                        boxShadow: '2px 0 6px -3px rgba(0,0,0,0.14)',
                                        borderRight: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    Ingredient
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        minWidth: 80,
                                        width: 80,
                                        fontWeight: 700,
                                        bgcolor: 'grey.100',
                                        borderRight: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    Unit
                                </TableCell>

                                {/* All dynamic EQ columns */}
                                {eqColumns.map(col => (
                                    <TableCell
                                        key={col.key}
                                        align="center"
                                        sx={{
                                            minWidth: 55,
                                            width: 60,
                                            p: 0.5,
                                            borderLeft: '1px solid',
                                            borderColor: 'grey.300',
                                        }}
                                    >
                                        <Chip
                                            label={col.label}
                                            size="small"
                                            sx={{
                                                height: 22,
                                                fontSize: '0.73rem',
                                                fontWeight: 700,
                                                minWidth: 48,
                                                px: 0.8,
                                                bgcolor: '#635bff',
                                                color: 'white',
                                                borderRadius: '10px',
                                                boxShadow: '0 1px 3px rgba(25,118,210,0.2)',
                                            }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {bomData.map((row, idx) => (
                                <TableRow
                                    key={idx}
                                    hover
                                    sx={{
                                        '&:nth-of-type(odd)': { bgcolor: 'grey.25' },
                                        '&:hover': { bgcolor: 'primary.50' },
                                        transition: 'background-color 0.1s',
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            position: 'sticky',
                                            left: 0,
                                            bgcolor: 'background.paper',
                                            zIndex: 10,
                                            fontWeight: 500,
                                            boxShadow: '2px 0 5px -3px rgba(0,0,0,0.1)',
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {row.ItmName}
                                    </TableCell>

                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontSize: '0.84rem',
                                            color: 'text.primary',
                                            borderRight: '1px solid',
                                            borderColor: 'grey.200',
                                        }}
                                    >
                                        {row.UnitName || '—'}
                                    </TableCell>

                                    {eqColumns.map((col, colIdx) => (
                                        <TableCell
                                            key={col.key}
                                            align="center"
                                            sx={{ borderLeft: '1px solid', borderColor: 'grey.200' }}
                                        >
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={row[col.key]}
                                                onChange={e => handleQtyChange(idx, col.key, e.target.value)}
                                                disabled={colIdx === 0}
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.1,
                                                    style: {
                                                        textAlign: 'center',
                                                        fontSize: '0.84rem',
                                                        padding: '4px 6px',
                                                        height: 24,
                                                    },
                                                }}
                                                onKeyPress={(e) => {
                                                    if (e.key === '.' || e.key === ',') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                sx={{
                                                    width: 56,
                                                    '& .MuiOutlinedInput-root': {
                                                        height: 28,
                                                        borderRadius: 1,
                                                        bgcolor: colIdx === 0
                                                            ? 'grey.100'
                                                            : (row[col.key] > 0 ? 'success.50' : 'transparent'),
                                                        '& fieldset': { borderColor: 'grey.300' },
                                                        '&:hover fieldset': { borderColor: colIdx === 0 ? 'grey.400' : 'primary.main' },
                                                        '&.Mui-focused fieldset': { borderColor: colIdx === 0 ? 'grey.400' : 'primary.main', borderWidth: 1.5 },
                                                        '&.Mui-disabled': {
                                                            bgcolor: 'grey.100',
                                                            color: 'text.primary',
                                                            WebkitTextFillColor: 'text.primary',
                                                        },
                                                    },
                                                }}
                                                inputRef={el => (inputRefs.current[idx] = inputRefs.current[idx] || []) && (inputRefs.current[idx][colIdx] = el)}
                                                onKeyDown={e => handleKeyPress(e, idx, colIdx)}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}

                            {bomData.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={eqColumns.length + 2}
                                        sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}
                                    >
                                        No ingredients found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box
                    sx={{
                        p: 1.2,
                        bgcolor: 'grey.50',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <span>{bomData.length} ingredient{bomData.length !== 1 ? 's' : ''}</span>
                    <span>{eqColumns.length} quantity level{eqColumns.length !== 1 ? 's' : ''}</span>
                </Box>
            </Paper>

            <Box sx={{ display: 'none' }}>
                <div ref={contentRef}>
                    <UpdatePrint
                        bomData={bomData}
                        foodName={foodName}
                    />
                </div>
            </Box>

            <ConfirmationDialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleConfirmDelete}
                title="Confirm Deletion"
                message="Are you sure you want to delete this record.?"
            />
        </Box>
    );
};

export default UpdateBomQty;